import { useQuery } from '@tanstack/react-query'
import { Grid3x3 } from 'lucide-react'
import { convictionApi } from '../api/client'
import { THETA_DEFAULT } from '../lib/theta'
import './ConfidenceHeatmap.css'

interface ConfidenceHeatmapProps {
  accountId: number
  theta: number
}

function shortDate(iso: string): string {
  // 'YYYY-MM-DD' -> 'MM/DD'
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  return `${parts[1]}/${parts[2]}`
}

/** Cool -> warm ramp: low scores read as a calm sunken tone, scores
 *  approaching/crossing theta shade toward warn/loss so the exit-adjacent
 *  cells pop visually. Theme-aware via color-mix over CSS vars. */
function cellBackground(score: number): string {
  const t = Math.max(0, Math.min(1, score))
  if (t < 0.5) {
    // Low band: sunken -> brand tint, calm.
    const pct = Math.round((t / 0.5) * 100)
    return `color-mix(in srgb, var(--brand) ${pct * 0.35}%, var(--bg-sunken))`
  }
  // High band: brand tint -> warn -> loss as it climbs past theta.
  const pct = Math.round(((t - 0.5) / 0.5) * 100)
  return `color-mix(in srgb, var(--loss) ${pct}%, var(--warn) ${100 - pct}%)`
}

/**
 * Dense symbol x day heatmap of daily thesis-break confidence scores. The
 * hero visualization of the research "picking analytics" section — cells at
 * or above theta (the exit valve threshold) get a distinct outline so a
 * would-fire exit is visually unmistakable.
 */
export function ConfidenceHeatmap({ accountId, theta }: ConfidenceHeatmapProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['confidence-log', accountId, 30],
    queryFn: () => convictionApi.confidenceLog(accountId, 30),
    retry: 1,
    refetchInterval: 60000,
  })

  const thresh = Number.isFinite(theta) && theta > 0 ? theta : THETA_DEFAULT
  const rows = data?.rows ?? []

  const symbols = Array.from(new Set(rows.map((r) => r.symbol))).sort()
  const dates = Array.from(new Set(rows.map((r) => r.log_date))).sort()
  const bySymbolDate = new Map<string, number>()
  for (const r of rows) bySymbolDate.set(`${r.symbol}|${r.log_date}`, r.score)

  const hasData = symbols.length > 0 && dates.length > 0

  return (
    <div className="confidence-heatmap">
      <div className="confidence-heatmap-header">
        <h4 className="h4 confidence-heatmap-title">
          <Grid3x3 size={15} className="icon" aria-hidden="true" />
          Confidence heatmap · 30d
        </h4>
        <p className="caption">daily thesis-break confidence; ≥θ would fire an exit</p>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon"><Grid3x3 size={28} aria-hidden="true" /></div>
          <div className="empty-text">confidence log unavailable</div>
        </div>
      ) : !hasData ? (
        <div className="empty-state">
          <div className="empty-icon"><Grid3x3 size={28} aria-hidden="true" /></div>
          <div className="empty-text">no confidence log rows yet</div>
        </div>
      ) : (
        <>
          <div className="confidence-heatmap-scroll">
            <div
              className="confidence-heatmap-grid"
              style={{ gridTemplateColumns: `72px repeat(${dates.length}, 18px)` }}
            >
              <div className="confidence-heatmap-corner" />
              {dates.map((d) => (
                <div key={d} className="confidence-heatmap-date-label">{shortDate(d)}</div>
              ))}
              {symbols.map((sym) => (
                <div className="confidence-heatmap-row" key={sym} style={{ display: 'contents' }}>
                  <div className="confidence-heatmap-symbol mono">{sym}</div>
                  {dates.map((d) => {
                    const score = bySymbolDate.get(`${sym}|${d}`)
                    if (score === undefined) {
                      return (
                        <div
                          key={d}
                          className="confidence-heatmap-cell confidence-heatmap-cell--blank"
                          role="gridcell"
                          aria-label={`${sym} ${d}: no data`}
                          title={`${sym} ${d}: no data`}
                        />
                      )
                    }
                    const atOrOverTheta = score >= thresh
                    return (
                      <div
                        key={d}
                        className={`confidence-heatmap-cell${atOrOverTheta ? ' confidence-heatmap-cell--over' : ''}`}
                        style={{ background: cellBackground(score) }}
                        role="gridcell"
                        aria-label={`${sym} ${d}: confidence ${score.toFixed(2)}${atOrOverTheta ? ' (≥θ, would exit)' : ''}`}
                        title={`${sym} ${d}: ${score.toFixed(3)}${atOrOverTheta ? ' (≥ θ)' : ''}`}
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="confidence-heatmap-legend">
            <div className="confidence-heatmap-legend-ramp">
              <span className="caption">0.0</span>
              <div className="confidence-heatmap-ramp-swatch" aria-hidden="true" />
              <span className="caption">1.0</span>
            </div>
            <div className="confidence-heatmap-legend-theta">
              <span className="confidence-heatmap-legend-theta-swatch" aria-hidden="true" />
              <span className="caption">≥ θ ({thresh.toFixed(2)}) — would fire exit</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
